// ExecDiscipline.js — "ความประพฤติและการลา" (pos 90/99)
//
// คำถามของหน้านี้: วินัยแย่ลงไหม ความผิดแบบไหนเพิ่มขึ้น และใครออกนอกโรงเรียนบ่อยผิดปกติ
//
// คะแนนความประพฤติคิดแบบเดียวกับที่ฝ่ายปกครองแสดง: 100 ลบด้วยที่ถูกหัก บวกคืนด้วย
// งานจิตอาสาที่ทำเสร็จแล้ว — เด็กที่ชดใช้ไปแล้วไม่ควรค้างอยู่ในรายงานของผู้บริหาร
import React, { useMemo, useState } from "react";
import { Badge, Col, ProgressBar, Row, Table } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import {
  ExecPage, Panel, SectionTitle, StatCard, EmptyState, ScrollTable,
  YearPicker, RangePicker, useExecYear, useExecData, compareTo,
  fmtNum, toNum, fmtThaiDate,
} from "./ExecShell";

const SOURCE_LABELS = {
  advisor: "ครูประจำชั้น",
  discipline: "ฝ่ายปกครอง",
  academic: "ฝ่ายวิชาการ",
  islamic_advisor: "ครูประจำชั้นศาสนา",
};

const LEAVE_TYPE_LABELS = { sick: "ลาป่วย", personal: "ลากิจ" };

const RULE_COLORS = ["#dc3545", "#fd7e14", "#6f42c1", "#0d6efd", "#20c997"];

export default function ExecDiscipline() {
  const { years, yearId, setYearId } = useExecYear();
  const [rangeKey, setRangeKey] = useState("90");
  const [rangeDays, setRangeDays] = useState(90);

  const query = useMemo(() => {
    const today = new Date();
    const to = today.toLocaleDateString("en-CA");
    const fromDate = new Date(today);
    fromDate.setDate(today.getDate() - (rangeDays - 1));
    return { year_id: yearId, from: fromDate.toLocaleDateString("en-CA"), to };
  }, [yearId, rangeDays]);

  const { data, loading, error } = useExecData("/api/executive/stats/discipline", query);

  const conduct = data?.conduct || { available: false };
  const current = conduct.current || {};
  const previous = conduct.previous || null;
  const deductions = useMemo(() => (data?.deductions || { available: false, months: [], rules: [], matrix: {} }), [data]);
  const merits = data?.merits || { available: false };
  const homeLeave = useMemo(() => (data?.homeLeave || { available: false, weeks: [], overdue: [] }), [data]);
  const frequentLeavers = Array.isArray(data?.frequentLeavers) ? data.frequentLeavers : [];
  const outing = data?.outing || { available: false };
  const leaveBySource = data?.leaveBySource || { available: false, bySource: [], byType: [] };

  // แท่งซ้อนรายเดือน แยกตามกฎ — เห็นว่าเดือนนี้ความผิดแบบไหนพุ่ง
  const deductionSeries = useMemo(() => (
    deductions.rules.map((rule) => ({
      name: rule.rule_name,
      data: deductions.months.map((month) => toNum(deductions.matrix[`${month}:${rule.rule_name}`])),
    }))
  ), [deductions]);

  const deductionOptions = useMemo(() => ({
    chart: { type: "bar", stacked: true, toolbar: { show: false } },
    colors: RULE_COLORS,
    plotOptions: { bar: { borderRadius: 3, columnWidth: "52%" } },
    dataLabels: { enabled: false },
    legend: { position: "top", fontSize: "11px" },
    xaxis: { categories: deductions.months, labels: { style: { fontSize: "11px" } } },
    yaxis: { labels: { style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
    tooltip: { shared: true, intersect: false },
  }), [deductions.months]);

  const homeLeaveOptions = useMemo(() => ({
    chart: { type: "bar", stacked: true, toolbar: { show: false } },
    colors: ["#0d6efd", "#d63384"],
    plotOptions: { bar: { borderRadius: 3, columnWidth: "56%" } },
    dataLabels: { enabled: false },
    legend: { position: "top", fontSize: "12px" },
    xaxis: {
      categories: homeLeave.weeks.map((w) => fmtThaiDate(w.week_start)),
      labels: { style: { fontSize: "10px" }, rotate: -45, hideOverlappingLabels: true },
    },
    yaxis: { labels: { style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
    tooltip: { shared: true, intersect: false },
  }), [homeLeave.weeks]);

  return (
    <ExecPage
      title="ความประพฤติและการลา"
      icon="ri-shield-check-line"
      lead="คะแนนความประพฤติ ความผิดที่พบบ่อย งานจิตอาสา และการออกนอกโรงเรียน"
      activeYear={data?.year}
      loading={loading}
      error={error}
      toolbar={
        <>
          <RangePicker value={rangeKey} onChange={(key, days) => { setRangeKey(key); setRangeDays(days); }} />
          <YearPicker years={years} yearId={yearId} onChange={setYearId} activeYear={data?.year} />
        </>
      }
    >
      <Row className="g-3 mb-4">
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-alarm-warning-fill" color="danger"
            label={`คะแนนต่ำกว่า ${conduct.thresholds?.low ?? 70}`}
            value={conduct.available ? fmtNum(current.low) : "—"}
            sub="ระดับที่ต้องเชิญผู้ปกครอง"
            compare={previous ? compareTo(current.low, previous.low, { suffix: " คน", higherIsBetter: false }) : null}
            tone={toNum(current.low) > 0 ? "danger" : undefined}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-eye-fill" color="warning"
            label={`คะแนน ${conduct.thresholds?.low ?? 70}–${(conduct.thresholds?.watch ?? 90) - 1}`}
            value={conduct.available ? fmtNum(current.watch) : "—"}
            sub="ระดับเฝ้าระวัง"
            compare={previous ? compareTo(current.watch, previous.watch, { suffix: " คน", higherIsBetter: false }) : null}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-hand-heart-fill" color="success" label="คะแนนที่ได้คืนจากจิตอาสา"
            value={merits.available ? fmtNum(merits.pointsReturned) : "—"}
            sub={merits.available ? `งานเสร็จแล้ว ${fmtNum(merits.completed)} ครั้ง · ${fmtNum(merits.students)} คน` : "ยังไม่มีข้อมูล"}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-door-closed-fill"
            color={toNum(homeLeave.overdue?.length) > 0 ? "danger" : "secondary"}
            label="เลยกำหนดกลับ"
            value={fmtNum(homeLeave.overdue?.length)}
            sub="ออกไปแล้วยังไม่กลับเข้าโรงเรียน"
            tone={toNum(homeLeave.overdue?.length) > 0 ? "danger" : undefined}
          />
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xl={7}>
          <Panel title="การหักคะแนนรายเดือน" note="แยกตามกฎที่พบบ่อยที่สุด 5 อันดับ">
            {deductionSeries.length === 0 || deductions.months.length === 0 ? (
              <EmptyState message="ยังไม่มีการหักคะแนนในปีนี้" />
            ) : (
              <ReactApexChart options={deductionOptions} series={deductionSeries} type="bar" height={300} />
            )}
          </Panel>
        </Col>
        <Col xl={5}>
          <Panel title="ความผิดที่พบบ่อย" note="ทั้งปีการศึกษา">
            {deductions.rules.length === 0 ? <EmptyState compact /> : (
              <ScrollTable>
                <Table className="table-sm mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>กฎ</th>
                      <th className="text-end">ครั้ง</th>
                      <th className="text-end">คะแนนรวม</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deductions.rules.map((rule, index) => (
                      <tr key={rule.rule_name}>
                        <td>
                          <span
                            className="d-inline-block rounded me-2"
                            style={{ width: 8, height: 8, background: RULE_COLORS[index % RULE_COLORS.length] }}
                          />
                          {rule.rule_name}
                        </td>
                        <td className="text-end fw-semibold">{fmtNum(rule.records)}</td>
                        <td className="text-end text-danger">-{fmtNum(rule.points)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollTable>
            )}
            {merits.available && (
              <div className="border-top pt-3 mt-3">
                <div className="d-flex justify-content-between fs-sm">
                  <span className="text-secondary">งานจิตอาสาที่มอบหมายแล้วยังไม่เสร็จ</span>
                  <span className="fw-semibold">{fmtNum(merits.assigned)} ครั้ง</span>
                </div>
              </div>
            )}
          </Panel>
        </Col>
      </Row>

      <SectionTitle icon="ri-home-heart-line" title="การกลับบ้านและออกนอกโรงเรียน" note={`ช่วง ${rangeDays} วันล่าสุด`} color="info" />
      <Row className="g-3 mb-4">
        <Col xl={7}>
          <Panel title="ใบขออนุญาตกลับบ้านรายสัปดาห์" note="แยกชาย หญิง">
            {!homeLeave.available || homeLeave.weeks.length === 0 ? (
              <EmptyState message="ยังไม่มีใบขออนุญาตในช่วงนี้" />
            ) : (
              <ReactApexChart
                options={homeLeaveOptions}
                series={[
                  { name: "ชาย", data: homeLeave.weeks.map((w) => toNum(w.male)) },
                  { name: "หญิง", data: homeLeave.weeks.map((w) => toNum(w.female)) },
                ]}
                type="bar" height={300}
              />
            )}
          </Panel>
        </Col>
        <Col xl={5}>
          <Panel title="นักเรียนที่กลับบ้านบ่อยที่สุด" note="ในช่วงที่เลือก">
            {frequentLeavers.length === 0 ? <EmptyState compact /> : (
              <ScrollTable>
                <Table className="table-sm mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>นักเรียน</th>
                      <th>ห้อง</th>
                      <th className="text-end">ครั้ง</th>
                      <th className="text-end">วัน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {frequentLeavers.map((row) => (
                      <tr key={row.st_id}>
                        <td className="text-truncate" style={{ maxWidth: 160 }}>{row.st_name}</td>
                        <td className="fs-xs text-secondary">{row.room_label || "—"}</td>
                        <td className="text-end fw-semibold">{fmtNum(row.times)}</td>
                        <td className="text-end">{fmtNum(row.days)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollTable>
            )}
          </Panel>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xl={5}>
          <Panel title="ยังไม่กลับเข้าโรงเรียน" note="อนุมัติแล้ว ออกไปแล้ว แต่เลยวันกลับ">
            {(homeLeave.overdue || []).length === 0 ? (
              <EmptyState message="ไม่มีนักเรียนค้างกลับ" compact />
            ) : homeLeave.overdue.map((row) => (
              <div key={row.leave_id} className="d-flex align-items-center justify-content-between border-bottom py-2 fs-sm">
                <span className="text-truncate">
                  {row.st_name}
                  <span className="text-secondary fs-xs ms-2">กลับ {fmtThaiDate(row.return_date)}</span>
                </span>
                <Badge bg="danger" className="flex-shrink-0">เกิน {fmtNum(row.days_late)} วัน</Badge>
              </div>
            ))}
            {outing.available && (
              <div className="border-top pt-3 mt-3 fs-sm">
                <div className="d-flex justify-content-between">
                  <span className="text-secondary">ออกนอกโรงเรียนระหว่างวัน</span>
                  <span className="fw-semibold">{fmtNum(outing.total)} ครั้ง</span>
                </div>
                {toNum(outing.notReturned) > 0 && (
                  <div className="d-flex justify-content-between mt-1 text-danger">
                    <span>ไม่ได้กดรับกลับเข้า</span>
                    <span className="fw-semibold">{fmtNum(outing.notReturned)} ครั้ง</span>
                  </div>
                )}
              </div>
            )}
          </Panel>
        </Col>
        <Col xl={7}>
          <Panel title="การแจ้งลาของนักเรียน" note="สี่ฝ่ายแจ้งลงตารางเดียวกัน สัดส่วนบอกว่าฝ่ายไหนใช้ระบบจริง">
            {!leaveBySource.available || leaveBySource.bySource.length === 0 ? (
              <EmptyState message="ยังไม่มีการแจ้งลาในช่วงนี้" compact />
            ) : (
              <>
                {leaveBySource.bySource.map((row) => {
                  const total = leaveBySource.bySource.reduce((sum, r) => sum + toNum(r.total), 0);
                  const percent = total > 0 ? (toNum(row.total) / total) * 100 : 0;
                  return (
                    <div key={row.source} className="mb-3">
                      <div className="d-flex justify-content-between fs-sm mb-1">
                        <span>{SOURCE_LABELS[row.source] || row.source}</span>
                        <span className="fw-semibold">{fmtNum(row.total)} ครั้ง</span>
                      </div>
                      <ProgressBar style={{ height: 7 }} now={percent} variant="info" />
                    </div>
                  );
                })}
                <div className="border-top pt-3 mt-1 d-flex gap-4 fs-sm">
                  {leaveBySource.byType.map((row) => (
                    <span key={row.leave_type}>
                      <span className="text-secondary">{LEAVE_TYPE_LABELS[row.leave_type] || row.leave_type}</span>
                      <span className="fw-semibold ms-2">{fmtNum(row.total)}</span>
                    </span>
                  ))}
                </div>
              </>
            )}
          </Panel>
        </Col>
      </Row>
    </ExecPage>
  );
}
